import type { Exercise, SlotTag } from '../exercise-types';
import type { MuscleId } from '../muscles';
import { PUSH_EXERCISES } from './push';
import { PULL_EXERCISES } from './pull';
import { LEG_EXERCISES } from './legs';
import { CORE_EXERCISES } from './core';

export const EXERCISES: Exercise[] = [
  ...PUSH_EXERCISES,
  ...PULL_EXERCISES,
  ...LEG_EXERCISES,
  ...CORE_EXERCISES,
];

export const EXERCISE_BY_ID: Record<string, Exercise> = Object.fromEntries(
  EXERCISES.map((e) => [e.id, e]),
);

export function getExercise(id: string): Exercise | undefined {
  return EXERCISE_BY_ID[id];
}

/** Exercises you can actually perform with the equipment you have ticked. */
export function availableExercises(equipmentIds: Set<string>): Exercise[] {
  return EXERCISES.filter((e) => e.equipment.every((eq) => equipmentIds.has(eq)));
}

export function exercisesForSlot(slot: SlotTag, equipmentIds: Set<string>): Exercise[] {
  return availableExercises(equipmentIds).filter((e) => e.slots.includes(slot));
}

export function exercisesForMuscle(muscle: MuscleId): Exercise[] {
  return EXERCISES.filter(
    (e) => e.primary.includes(muscle) || e.secondary.includes(muscle),
  );
}

/**
 * Data integrity check — every variation must point at a real exercise and
 * every equipment id at a real piece of kit. Run by the test suite so a typo
 * in the library fails CI instead of silently showing a broken link.
 */
export function validateLibrary(knownEquipment: Set<string>): string[] {
  const problems: string[] = [];
  const ids = new Set(EXERCISES.map((e) => e.id));

  const seen = new Set<string>();
  for (const ex of EXERCISES) {
    if (seen.has(ex.id)) problems.push(`duplicate exercise id: ${ex.id}`);
    seen.add(ex.id);

    for (const v of ex.variations ?? []) {
      if (!ids.has(v)) problems.push(`${ex.id}: unknown variation "${v}"`);
      if (v === ex.id) problems.push(`${ex.id}: lists itself as a variation`);
    }
    for (const eq of ex.equipment) {
      if (!knownEquipment.has(eq)) problems.push(`${ex.id}: unknown equipment "${eq}"`);
    }
    if (ex.slots.length === 0) problems.push(`${ex.id}: has no slots`);
    if (ex.primary.length === 0) problems.push(`${ex.id}: has no primary muscle`);
    if (ex.repRange[0] > ex.repRange[1]) problems.push(`${ex.id}: inverted rep range`);
  }
  return problems;
}
